import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Send,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  Film,
  Image,
  Video,
  BookOpen,
  Layers,
  BarChart3,
  TableProperties,
  LayoutGrid,
  Paperclip,
  X,
} from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || '/api/v1'

interface SharedContent {
  id: string
  title: string
  body?: string
  caption?: string
  hashtags?: string
  type: string
  platforms: string[]
  status: string
  scheduledAt?: string
  socialFormatId?: string | null
  socialFormat?: SharedFormat | null
  assets: { id: string; filename: string; mimeType: string; type: string }[]
  contentComments: {
    id: string
    text: string
    type: string
    createdAt: string
    authorName?: string | null
    user: { firstName: string; lastName: string } | null
  }[]
  assignedTo?: { firstName: string; lastName: string } | null
}

interface SharedPlan {
  id: string
  title: string
  project: { id: string; name: string }
  client: { id: string; name: string; logo?: string }
}

interface SharedFormat {
  id: string
  name: string
  description?: string | null
  type: string
  platforms: string[]
  specs?: Record<string, unknown> | null
  guidelines?: string | null
  isActive: boolean
  sortOrder: number
}

// Stati del flusso di lavoro dei contenuti. Gli stati con un significato
// semantico (Approvato, Modifiche richieste, Pubblicato) usano i token di
// sistema; le fasi di workflow restano tinte categoriali distinguibili.
const STATUS_LABELS: Record<string, { label: string; color: string; bg: string; dot: string }> = {
  IDEA: { label: 'Idea', color: 'text-slate-600', bg: 'bg-slate-100', dot: 'bg-slate-400' },
  TODO: { label: 'Da fare', color: 'text-primary-700', bg: 'bg-primary-50', dot: 'bg-primary-400' },
  IN_PRODUCTION: { label: 'In produzione', color: 'text-yellow-700', bg: 'bg-yellow-100', dot: 'bg-yellow-400' },
  READY: { label: 'Pronto', color: 'text-purple-700', bg: 'bg-purple-100', dot: 'bg-purple-400' },
  IN_REVIEW: { label: 'In revisione', color: 'text-warning-600', bg: 'bg-warning-50', dot: 'bg-warning-400' },
  CHANGES_REQUESTED: { label: 'Modifiche richieste', color: 'text-danger-600', bg: 'bg-danger-50', dot: 'bg-danger-500' },
  APPROVED: { label: 'Approvato', color: 'text-success-700', bg: 'bg-success-50', dot: 'bg-success-500' },
  SCHEDULED: { label: 'Programmato', color: 'text-indigo-700', bg: 'bg-indigo-100', dot: 'bg-indigo-400' },
  PUBLISHED: { label: 'Pubblicato', color: 'text-success-700', bg: 'bg-success-50', dot: 'bg-success-500' },
}

const TYPE_ICONS: Record<string, typeof FileText> = {
  POST: FileText,
  REEL: Film,
  STORY: Image,
  VIDEO: Video,
  GUIDE: BookOpen,
  AD: Layers,
  CAROUSEL: Layers,
  ARTICLE: BookOpen,
  INFOGRAPHIC: BarChart3,
}

const PLATFORM_COLORS_BG: Record<string, string> = {
  INSTAGRAM: 'bg-pink-100 text-pink-700',
  FACEBOOK: 'bg-blue-100 text-blue-700',
  TIKTOK: 'bg-slate-800 text-white',
  LINKEDIN: 'bg-sky-100 text-sky-700',
  YOUTUBE: 'bg-red-100 text-red-700',
  TWITTER: 'bg-slate-100 text-slate-700',
  PINTEREST: 'bg-rose-100 text-rose-700',
}

const PLATFORM_DOT_COLORS: Record<string, string> = {
  INSTAGRAM: '#E1306C',
  FACEBOOK: '#1877F2',
  TIKTOK: '#1a1a1a',
  LINKEDIN: '#0A66C2',
  YOUTUBE: '#FF0000',
  TWITTER: '#1DA1F2',
  PINTEREST: '#E60023',
}

const PLATFORM_LABELS: Record<string, string> = {
  INSTAGRAM: 'IG',
  FACEBOOK: 'FB',
  TIKTOK: 'TT',
  LINKEDIN: 'LI',
  YOUTUBE: 'YT',
  TWITTER: 'X',
  PINTEREST: 'PI',
}

const FORMAT_TYPE_META: Record<string, { label: string; icon: typeof FileText; color: string }> = {
  POST: { label: 'Post', icon: FileText, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  REEL: { label: 'Reel', icon: Film, color: 'bg-pink-50 text-pink-700 border-pink-200' },
  STORY: { label: 'Story', icon: Image, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  CAROUSEL: { label: 'Carosello', icon: Layers, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  VIDEO: { label: 'Video', icon: Video, color: 'bg-red-50 text-red-700 border-red-200' },
  ARTICLE: { label: 'Articolo', icon: BookOpen, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  INFOGRAPHIC: { label: 'Infografica', icon: BarChart3, color: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
}

const FORMAT_PLATFORM_LABELS: Record<string, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  TIKTOK: 'TikTok',
  LINKEDIN: 'LinkedIn',
  YOUTUBE: 'YouTube',
  TWITTER: 'X / Twitter',
  PINTEREST: 'Pinterest',
}

type ViewMode = 'calendar' | 'table' | 'format'

export function SharedPEDPage() {
  const { token } = useParams<{ token: string }>()
  const [plan, setPlan] = useState<SharedPlan | null>(null)
  const [contents, setContents] = useState<SharedContent[]>([])
  const [formats, setFormats] = useState<SharedFormat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedContent, setSelectedContent] = useState<SharedContent | null>(null)
  const [viewMode, setViewMode] = useState<ViewMode>('calendar')
  const [selectedFormat, setSelectedFormat] = useState<SharedFormat | null>(null)

  // Calendar state
  const today = new Date()
  const [currentYear, setCurrentYear] = useState(today.getFullYear())
  const [currentMonth, setCurrentMonth] = useState(today.getMonth())

  // Comment form
  const [commentText, setCommentText] = useState('')
  const [authorName, setAuthorName] = useState(() => localStorage.getItem('shared-ped-author') || '')
  const [submitting, setSubmitting] = useState(false)

  const loadData = async () => {
    if (!token) return
    try {
      const r = await fetch(`${API_BASE}/public/ped/${token}`)
      if (!r.ok) throw new Error('Link non valido o scaduto')
      const res = await r.json()
      const data = res.data ?? res
      setPlan(data.plan)
      setContents(data.contents ?? [])
      setFormats(data.formats ?? [])
    } catch (err: any) {
      setError(err.message)
    }
  }

  useEffect(() => {
    setLoading(true)
    loadData().finally(() => setLoading(false))
  }, [token])

  const submitComment = async (contentId: string, type: 'comment' | 'approval' | 'change_request') => {
    if (!token || (!commentText.trim() && type === 'comment') || !authorName.trim()) return
    setSubmitting(true)
    localStorage.setItem('shared-ped-author', authorName)
    try {
      const body = {
        text: type === 'approval' ? 'Approvato' : type === 'change_request' ? (commentText.trim() || 'Richiesta modifiche') : commentText.trim(),
        authorName: authorName.trim(),
        type,
      }
      const r = await fetch(`${API_BASE}/public/ped/${token}/contents/${contentId}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!r.ok) throw new Error('Errore')
      // Reload data
      const res = await fetch(`${API_BASE}/public/ped/${token}`).then((r) => r.json())
      const data = res.data ?? res
      setContents(data.contents ?? [])
      const updated = (data.contents ?? []).find((c: SharedContent) => c.id === contentId)
      if (updated) setSelectedContent(updated)
      setCommentText('')
    } catch {
      alert('Errore nell\'invio del commento')
    } finally {
      setSubmitting(false)
    }
  }

  // Calendar helpers
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
  const firstDayOfWeek = (new Date(currentYear, currentMonth, 1).getDay() + 6) % 7
  const monthLabel = new Date(currentYear, currentMonth).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })

  const contentsByDay: Record<number, SharedContent[]> = {}
  for (const c of contents) {
    if (!c.scheduledAt) continue
    const d = new Date(c.scheduledAt)
    if (d.getFullYear() === currentYear && d.getMonth() === currentMonth) {
      const day = d.getDate()
      if (!contentsByDay[day]) contentsByDay[day] = []
      contentsByDay[day].push(c)
    }
  }

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear((y) => y - 1) }
    else setCurrentMonth((m) => m - 1)
  }
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear((y) => y + 1) }
    else setCurrentMonth((m) => m + 1)
  }

  // Table helpers
  const sortedContents = [...contents].sort((a, b) => {
    const da = a.scheduledAt ? new Date(a.scheduledAt).getTime() : 0
    const db = b.scheduledAt ? new Date(b.scheduledAt).getTime() : 0
    return da - db
  })

  const formatDate = (d?: string) => {
    if (!d) return '—'
    try {
      return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    } catch { return d }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (error || !plan) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center bg-white border border-slate-200 rounded-2xl shadow-card px-8 py-10 max-w-sm">
          <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-lg font-medium text-slate-700">{error || 'Link non valido'}</p>
          <p className="text-sm text-slate-400 mt-1">Verifica il link con il tuo referente</p>
        </div>
      </div>
    )
  }

  const StatusBadge = ({ status }: { status: string }) => {
    const s = STATUS_LABELS[status] ?? { label: status, color: 'text-slate-600', bg: 'bg-slate-100' }
    return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${s.color} ${s.bg}`}>{s.label}</span>
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-slate-900 truncate">{plan.project.name}</h1>
            <p className="text-sm text-slate-500">{plan.client.name} — Piano Editoriale</p>
          </div>

          <div className="flex items-center gap-3">
            {/* View mode toggle */}
            <div className="flex items-center bg-slate-100 rounded-full p-1">
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-1.5 px-3.5 py-1 text-xs font-semibold rounded-full transition-colors ${viewMode === 'calendar' ? 'bg-white text-primary-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <Calendar className="w-3.5 h-3.5" />
                Calendario
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3.5 py-1 text-xs font-semibold rounded-full transition-colors ${viewMode === 'table' ? 'bg-white text-primary-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <TableProperties className="w-3.5 h-3.5" />
                Tabella
              </button>
              {formats.length > 0 && (
                <button
                  onClick={() => setViewMode('format')}
                  className={`flex items-center gap-1.5 px-3.5 py-1 text-xs font-semibold rounded-full transition-colors ${viewMode === 'format' ? 'bg-white text-primary-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  <LayoutGrid className="w-3.5 h-3.5" />
                  Format
                </button>
              )}
            </div>

            {/* Month navigation (calendar view) */}
            {viewMode === 'calendar' && (
              <div className="flex items-center gap-2">
                <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-full text-slate-500 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-700 transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-bold tracking-tight text-slate-700 capitalize min-w-[140px] text-center tabular-nums">{monthLabel}</span>
                <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-full text-slate-500 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-700 transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* ═══ CALENDAR VIEW ═══ */}
        {viewMode === 'calendar' && (
          <>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
              <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
                {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((d) => (
                  <div key={d} className="px-2 py-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em] text-center">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                  <div key={`empty-${i}`} className="min-h-[100px] border-b border-r border-slate-200 bg-slate-50/50" />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1
                  const dayContents = contentsByDay[day] ?? []
                  const isToday = day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear()
                  return (
                    <div key={day} className={`min-h-[100px] border-b border-r border-slate-200 p-1.5 ${isToday ? 'bg-primary-50/50' : ''}`}>
                      <span className={`text-xs font-semibold tabular-nums ${isToday ? 'text-white bg-primary-600 rounded-full w-6 h-6 inline-flex items-center justify-center' : 'text-slate-600'}`}>
                        {day}
                      </span>
                      <div className="mt-1 space-y-0.5">
                        {dayContents.map((c) => {
                          const Icon = TYPE_ICONS[c.type] ?? FileText
                          return (
                            <button
                              key={c.id}
                              onClick={() => setSelectedContent(c)}
                              className="w-full text-left px-1.5 py-1 rounded-md text-xs truncate border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-colors flex items-center gap-1"
                            >
                              <Icon className="w-3 h-3 flex-shrink-0 text-slate-400" />
                              <span className="truncate font-medium text-slate-700">{c.title}</span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Unscheduled contents */}
            {contents.filter((c) => !c.scheduledAt).length > 0 && (
              <div className="mt-6">
                <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em] mb-2.5">Senza data programmata</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {contents.filter((c) => !c.scheduledAt).map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedContent(c)}
                      className="text-left bg-white rounded-2xl border border-slate-200 shadow-card p-4 hover:border-slate-300 transition-colors"
                    >
                      <p className="text-sm font-medium text-slate-800 truncate">{c.title}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <StatusBadge status={c.status} />
                        {c.platforms.map((p) => (
                          <span key={p} className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${PLATFORM_COLORS_BG[p] ?? 'bg-slate-100 text-slate-600'}`}>{p}</span>
                        ))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ═══ TABLE VIEW ═══ */}
        {viewMode === 'table' && (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-card overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {['Data', 'Format', 'Titolo', 'Piattaforma', 'Tipo', 'Status', 'Caption', 'Hashtag'].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedContents.map((c) => {
                  const now = new Date()
                  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                  const tomorrowStart = new Date(todayStart.getTime() + 86400000)
                  const scheduled = c.scheduledAt ? new Date(c.scheduledAt) : null
                  const isPublished = c.status === 'PUBLISHED'
                  const isReadyToPublish = c.status === 'APPROVED' || c.status === 'SCHEDULED'
                  const isOverdue = !!(scheduled && scheduled < now && !isPublished && !isReadyToPublish)
                  const isTodayItem = !!(scheduled && scheduled >= todayStart && scheduled < tomorrowStart)

                  const rowBg = isPublished
                    ? 'bg-success-50/50 hover:bg-success-50/80'
                    : isOverdue
                      ? 'bg-danger-50/40 hover:bg-danger-50/60'
                      : 'hover:bg-slate-50'

                  const borderLeft = isPublished
                    ? 'border-l-2 border-l-success-500'
                    : isOverdue
                      ? 'border-l-2 border-l-danger-500'
                      : isReadyToPublish
                        ? 'border-l-2 border-l-success-500'
                        : isTodayItem
                          ? 'border-l-2 border-l-primary-500'
                          : ''

                  const cellBase = 'px-3 py-2 text-sm text-slate-700 border-b border-slate-200'

                  return (
                    <tr key={c.id} className={`transition-colors cursor-pointer ${rowBg}`} onClick={() => setSelectedContent(c)}>
                      <td className={`${cellBase} w-[160px] whitespace-nowrap tabular-nums ${borderLeft}`}>
                        <span className={isOverdue ? 'text-danger-600 font-semibold' : isTodayItem ? 'text-primary-600 font-semibold' : ''}>
                          {formatDate(c.scheduledAt)}
                          {isOverdue && <span className="ml-1 text-[10px]">!!</span>}
                        </span>
                      </td>
                      <td className={`${cellBase} min-w-[120px]`}>
                        <span className="text-sm">{c.socialFormat?.name ?? '—'}</span>
                      </td>
                      <td className={`${cellBase} min-w-[180px] font-medium`}>
                        <span className="inline-flex items-center gap-1.5">
                          {c.title || '—'}
                          {(c.assets?.length ?? 0) > 0 && (
                            <span className="inline-flex items-center gap-0.5 text-slate-400" title={`${c.assets.length} allegat${c.assets.length === 1 ? 'o' : 'i'}`}>
                              <Paperclip className="w-3 h-3" />
                              <span className="text-[10px] font-medium">{c.assets.length}</span>
                            </span>
                          )}
                        </span>
                      </td>
                      <td className={`${cellBase} w-[130px]`}>
                        <div className="flex items-center gap-1 flex-wrap">
                          {(c.platforms ?? []).map((p) => (
                            <span key={p} className="inline-flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PLATFORM_DOT_COLORS[p] ?? '#94a3b8' }} />
                              <span className="text-xs font-medium">{PLATFORM_LABELS[p] ?? p}</span>
                            </span>
                          ))}
                          {(!c.platforms || c.platforms.length === 0) && <span className="text-xs text-slate-400">—</span>}
                        </div>
                      </td>
                      <td className={`${cellBase} w-[100px]`}>
                        <span className="text-xs">{FORMAT_TYPE_META[c.type]?.label ?? c.type}</span>
                      </td>
                      <td className={`${cellBase} w-[130px]`}>
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_LABELS[c.status]?.dot ?? 'bg-slate-400'}`} />
                          <span className="text-xs font-medium">{STATUS_LABELS[c.status]?.label ?? c.status}</span>
                        </span>
                      </td>
                      <td className={`${cellBase} max-w-[200px]`}>
                        <span className="truncate block text-xs text-slate-500">{c.caption || '—'}</span>
                      </td>
                      <td className={`${cellBase} max-w-[150px]`}>
                        <span className="truncate block text-xs text-primary-600">{c.hashtags || '—'}</span>
                      </td>
                    </tr>
                  )
                })}
                {sortedContents.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-400">
                      Nessun contenuto nel piano editoriale
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ═══ FORMAT VIEW ═══ */}
        {viewMode === 'format' && (
          <div>
            {formats.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-slate-200 shadow-card">
                <LayoutGrid className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-sm font-medium text-slate-900">Nessun format disponibile</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {formats.map((fmt) => {
                  const specs = fmt.specs as Record<string, string | number | undefined> | null | undefined
                  const allTypes: string[] = (specs as any)?.types && Array.isArray((specs as any).types) ? (specs as any).types as string[] : [fmt.type]
                  const firstMeta = FORMAT_TYPE_META[allTypes[0] ?? 'POST'] ?? FORMAT_TYPE_META['POST']!
                  const Icon = firstMeta.icon

                  return (
                    <div key={fmt.id} onClick={() => setSelectedFormat(fmt)} className="bg-white rounded-2xl border border-slate-200 shadow-card flex flex-col overflow-hidden cursor-pointer hover:border-slate-300 transition-colors">
                      <div className={`px-4 py-3 flex items-center gap-2 border-b ${firstMeta.color}`}>
                        <Icon className="w-4 h-4" />
                        <span className="text-xs font-semibold uppercase tracking-wide">
                          {allTypes.map((t) => FORMAT_TYPE_META[t]?.label ?? t).join(' / ')}
                        </span>
                      </div>
                      <div className="p-4 flex flex-col flex-1 gap-2">
                        <h3 className="text-sm font-semibold text-slate-900">{fmt.name}</h3>
                        {fmt.description && <p className="text-xs text-slate-500 line-clamp-2">{fmt.description}</p>}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {fmt.platforms.map((p) => (
                            <span key={p} className="px-1.5 py-0.5 text-[10px] font-medium rounded-md bg-slate-100 text-slate-600">
                              {FORMAT_PLATFORM_LABELS[p] ?? p}
                            </span>
                          ))}
                        </div>
                        {fmt.guidelines && <p className="text-[11px] text-slate-400 line-clamp-2 mt-1 italic">{fmt.guidelines}</p>}
                        {specs && (
                          <div className="flex flex-wrap gap-2 mt-1">
                            {specs['width'] && specs['height'] && (
                              <span className="text-[10px] text-slate-400">{`${specs['width']}x${specs['height']}`}</span>
                            )}
                            {specs['aspectRatio'] && (
                              <span className="text-[10px] text-slate-400">{`${specs['aspectRatio']}`}</span>
                            )}
                            {specs['duration'] && (
                              <span className="text-[10px] text-slate-400">{`${specs['duration']}`}</span>
                            )}
                            {specs['maxChars'] && (
                              <span className="text-[10px] text-slate-400">{`max ${specs['maxChars']} car.`}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content detail slide-over */}
      {selectedContent && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={() => setSelectedContent(null)} />
          <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-modal z-50 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-900 truncate pr-4">{selectedContent.title}</h3>
              <button onClick={() => setSelectedContent(null)} className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {/* Status & platforms */}
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={selectedContent.status} />
                <span className="text-xs text-slate-400">{FORMAT_TYPE_META[selectedContent.type]?.label ?? selectedContent.type}</span>
                {selectedContent.platforms.map((p) => (
                  <span key={p} className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${PLATFORM_COLORS_BG[p] ?? 'bg-slate-100 text-slate-600'}`}>{p}</span>
                ))}
              </div>

              {/* Format */}
              {selectedContent.socialFormat && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <LayoutGrid className="w-4 h-4 text-slate-400" />
                  <span>Format: <strong>{selectedContent.socialFormat.name}</strong></span>
                </div>
              )}

              {/* Scheduled date */}
              {selectedContent.scheduledAt && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="tabular-nums">{new Date(selectedContent.scheduledAt).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
              )}

              {/* Caption */}
              {selectedContent.caption && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Caption</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedContent.caption}</p>
                </div>
              )}

              {/* Hashtags */}
              {selectedContent.hashtags && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Hashtags</p>
                  <p className="text-sm text-primary-600">{selectedContent.hashtags}</p>
                </div>
              )}

              {/* Body/Notes */}
              {selectedContent.body && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Note</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedContent.body}</p>
                </div>
              )}

              {/* Assets */}
              {selectedContent.assets.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">Allegati</p>
                  <div className="space-y-1">
                    {selectedContent.assets.map((a) => (
                      <div key={a.id} className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 rounded-md px-3 py-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        {a.filename}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick actions */}
              {selectedContent.status !== 'APPROVED' && selectedContent.status !== 'PUBLISHED' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => submitComment(selectedContent.id, 'approval')}
                    disabled={submitting || !authorName.trim()}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-success-600 rounded-lg hover:bg-success-700 transition-colors disabled:opacity-50"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Approva
                  </button>
                  <button
                    onClick={() => {
                      if (!commentText.trim()) {
                        setCommentText('Richiesta modifiche: ')
                        return
                      }
                      submitComment(selectedContent.id, 'change_request')
                    }}
                    disabled={submitting || !authorName.trim()}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-warning-600 bg-warning-50 border border-warning-100 rounded-lg hover:bg-warning-100 transition-colors disabled:opacity-50"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    Richiedi modifiche
                  </button>
                </div>
              )}

              {/* Comments */}
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                  Commenti ({selectedContent.contentComments.length})
                </p>
                <div className="space-y-2 mb-4">
                  {selectedContent.contentComments.length === 0 && (
                    <p className="text-sm text-slate-400 italic">Nessun commento</p>
                  )}
                  {selectedContent.contentComments.map((c) => (
                    <div
                      key={c.id}
                      className={`rounded-lg px-3 py-2.5 text-sm ${
                        c.type === 'approval' ? 'bg-success-50 border border-success-100' :
                        c.type === 'change_request' ? 'bg-warning-50 border border-warning-100' :
                        'bg-slate-50 border border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-700">
                          {c.user ? `${c.user.firstName} ${c.user.lastName}` : c.authorName ?? 'Ospite'}
                        </span>
                        {c.type === 'approval' && <CheckCircle2 className="w-3.5 h-3.5 text-success-600" />}
                        {c.type === 'change_request' && <AlertTriangle className="w-3.5 h-3.5 text-warning-600" />}
                        <span className="text-xs text-slate-400 tabular-nums">
                          {new Date(c.createdAt).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-slate-600">{c.text}</p>
                    </div>
                  ))}
                </div>

                {/* Comment form */}
                <div className="space-y-2">
                  {!authorName && (
                    <input
                      type="text"
                      placeholder="Il tuo nome..."
                      value={authorName}
                      onChange={(e) => setAuthorName(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-colors"
                    />
                  )}
                  {authorName && (
                    <p className="text-xs text-slate-400">Commenta come <strong>{authorName}</strong> <button onClick={() => setAuthorName('')} className="text-primary-600 hover:underline ml-1">cambia</button></p>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Scrivi un commento..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && commentText.trim() && submitComment(selectedContent.id, 'comment')}
                      className="flex-1 px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-colors"
                    />
                    <button
                      onClick={() => submitComment(selectedContent.id, 'comment')}
                      disabled={submitting || !commentText.trim() || !authorName.trim()}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Format detail modal */}
      {selectedFormat && (() => {
        const specs = selectedFormat.specs as Record<string, string | number | undefined> | null | undefined
        const allTypes: string[] = (specs as any)?.types && Array.isArray((specs as any).types) ? (specs as any).types as string[] : [selectedFormat.type]
        const firstMeta = FORMAT_TYPE_META[allTypes[0] ?? 'POST'] ?? FORMAT_TYPE_META['POST']!
        const Icon = firstMeta.icon

        return (
          <>
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={() => setSelectedFormat(null)} />
            <div className="fixed inset-0 flex items-center justify-center z-50 p-4" onClick={() => setSelectedFormat(null)}>
              <div className="bg-white rounded-3xl shadow-modal w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                {/* Header colorato */}
                <div className={`px-6 py-4 flex items-center justify-between border-b ${firstMeta.color}`}>
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-semibold uppercase tracking-wide">
                      {allTypes.map((t) => FORMAT_TYPE_META[t]?.label ?? t).join(' / ')}
                    </span>
                  </div>
                  <button onClick={() => setSelectedFormat(null)} className="p-1 rounded-md hover:bg-black/10 transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="px-6 py-5 space-y-5">
                  {/* Nome */}
                  <h2 className="text-lg font-bold text-slate-900">{selectedFormat.name}</h2>

                  {/* Descrizione */}
                  {selectedFormat.description && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Descrizione</p>
                      <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{selectedFormat.description}</p>
                    </div>
                  )}

                  {/* Piattaforme */}
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Piattaforme</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedFormat.platforms.map((p) => (
                        <span key={p} className={`px-2.5 py-1 text-xs font-medium rounded-full ${PLATFORM_COLORS_BG[p] ?? 'bg-slate-100 text-slate-600'}`}>
                          {FORMAT_PLATFORM_LABELS[p] ?? p}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Specifiche tecniche */}
                  {specs && (Object.keys(specs).filter(k => k !== 'types' && specs[k]).length > 0) && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Specifiche Tecniche</p>
                      <div className="grid grid-cols-2 gap-2">
                        {specs['width'] && specs['height'] && (
                          <div className="bg-slate-50 rounded-lg px-3 py-2">
                            <p className="text-[10px] text-slate-400 uppercase">Dimensioni</p>
                            <p className="text-sm font-medium text-slate-700">{`${specs['width']} x ${specs['height']} px`}</p>
                          </div>
                        )}
                        {specs['aspectRatio'] && (
                          <div className="bg-slate-50 rounded-lg px-3 py-2">
                            <p className="text-[10px] text-slate-400 uppercase">Aspect Ratio</p>
                            <p className="text-sm font-medium text-slate-700">{`${specs['aspectRatio']}`}</p>
                          </div>
                        )}
                        {specs['duration'] && (
                          <div className="bg-slate-50 rounded-lg px-3 py-2">
                            <p className="text-[10px] text-slate-400 uppercase">Durata</p>
                            <p className="text-sm font-medium text-slate-700">{`${specs['duration']}`}</p>
                          </div>
                        )}
                        {specs['maxChars'] && (
                          <div className="bg-slate-50 rounded-lg px-3 py-2">
                            <p className="text-[10px] text-slate-400 uppercase">Max Caratteri</p>
                            <p className="text-sm font-medium text-slate-700">{`${specs['maxChars']}`}</p>
                          </div>
                        )}
                        {specs['slides'] && (
                          <div className="bg-slate-50 rounded-lg px-3 py-2">
                            <p className="text-[10px] text-slate-400 uppercase">Slide</p>
                            <p className="text-sm font-medium text-slate-700">{`${specs['slides']}`}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Linee guida */}
                  {selectedFormat.guidelines && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Linee Guida</p>
                      <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap bg-warning-50 border border-warning-100 rounded-lg px-4 py-3">{selectedFormat.guidelines}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )
      })()}
    </div>
  )
}
